import React, { forwardRef, type PropsWithChildren } from "react";

import * as RadixDialog from "@radix-ui/react-dialog";

import { Circle } from "../Circle";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/
const ROOT_NAME = "Modal";
type RootProps = RadixDialog.DialogProps;
const Root = RadixDialog.Root;
Root.displayName = ROOT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/
const TRIGGER_NAME = "ModalTrigger";
type TriggerProps = RadixDialog.DialogTriggerProps;
const Trigger = RadixDialog.Trigger;
Trigger.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/
const CLOSE_NAME = "ModalClose";
type CloseProps = RadixDialog.DialogCloseProps;
const Close = RadixDialog.Close;
Close.displayName = CLOSE_NAME;

/* -------------------------------------------------------------------------------------------------
 * Header
 * -----------------------------------------------------------------------------------------------*/
const HEADER_NAME = "DialogHeader";
interface HeaderProps extends PropsWithChildren {
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  className?: string;
}
const Header: React.FC<HeaderProps> = ({ title, subtitle, icon, className, children }) => (
  <div className={cn("mb-5 gap-5 md:mb-10 flex w-full items-center", className)}>
    <div className={"gap-5 mr-auto flex items-center"}>
      <Circle.Root className={"md:h-[70px]! md:w-[70px]! h-[50px]! w-[50px]!"} backgroundColor="gray" color="darkBlue">
        {icon}
      </Circle.Root>
      <div className={"tracking-[0]"}>
        <RadixDialog.Title asChild>
          <h2 className={"mb-0 font-semibold md:text-2xl text-xl text-primary-dark-blue"}>{title}</h2>
        </RadixDialog.Title>
        <h3 className={"mb-0 leading-6 font-normal text-base text-primary-dark-gray"}>{subtitle}</h3>
      </div>
    </div>
    {children}
  </div>
);

Header.displayName = HEADER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "ModalContent";
interface ContentProps extends RadixDialog.DialogContentProps {
  /** `overlay` styles the backdrop, which `className` cannot reach. */
  classNames?: { overlay?: string };
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ className, classNames, children, ...props }, forwardedRef) => (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={cn("overlay", classNames?.overlay)} />
      <RadixDialog.Content {...props} ref={forwardedRef} className={cn("p-2.5 md:p-10 content", className)}>
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  ),
);

Content.displayName = CONTENT_NAME;

export { Close, Content, Header, Root, Trigger };
export type { CloseProps, ContentProps, HeaderProps, RootProps, TriggerProps };
